import type { StorageDocument, StorageDocumentStatus, StorageDocumentType } from './storage-api';

/** Fixed order the "Mi empresa" screen always renders the three documents in (spec §4). */
export const STORAGE_DOCUMENT_TYPES: readonly StorageDocumentType[] = [
  'dgi_certificate',
  'fire_department_permit',
  'insurance_policy'
];

/** Exact wording from the design export's "Mi empresa" screen, confirmed against spec §4. */
export const STORAGE_DOCUMENT_LABELS: Record<StorageDocumentType, string> = {
  dgi_certificate: 'Certificado único DGI',
  fire_department_permit: 'Habilitación de bomberos',
  insurance_policy: 'Póliza de seguro del depósito'
};

export type StatusTone = 'neutral' | 'info' | 'positive' | 'caution' | 'critical';

export interface StatusPresentation {
  label: string;
  tone: StatusTone;
}

/** Mirrors `nexvia-backoffice/src/domain/kyc.ts`'s `kycStatusPresentation` shape (label + tone). */
export function documentStatusPresentation(status: StorageDocumentStatus): StatusPresentation {
  switch (status) {
    case 'uploading':
      return { label: 'Subiendo…', tone: 'info' };
    case 'pending_review':
      return { label: 'En revisión', tone: 'caution' };
    case 'approved':
      return { label: 'Aprobado', tone: 'positive' };
    case 'rejected':
      return { label: 'Rechazado', tone: 'critical' };
    case 'failed':
      return { label: 'No se pudo subir', tone: 'critical' };
    case 'outdated':
      return { label: 'Vencido', tone: 'caution' };
    default:
      // The column is a plain varchar server-side (same note as kycStatusPresentation's own) —
      // an unexpected value renders as itself rather than crashing or being silently relabelled.
      return { label: status, tone: 'neutral' };
  }
}

/**
 * NEX-1164 / NEX-1171 — a document the server still has as `uploading` while this tab is not uploading
 * it: the upload never finished (the file never reached Spaces — until 2026-10-28 the direct PUT was refused on CORS — or the tab
 * was closed midway), so "Subiendo…" would read as in progress forever. `DocumentUploadRow` shows this
 * instead, with "Volver a subir": a new upload replaces that row (`softDeleteReplaceable` server-side).
 */
export const STALLED_UPLOAD_PRESENTATION: StatusPresentation = { label: 'La subida no se completó', tone: 'critical' };

/** Today's calendar date, in the viewer's own local timezone, as `YYYY-MM-DD` (lexically
 *  comparable against the same shape). `getFullYear`/`getMonth`/`getDate` are local-timezone
 *  accessors on purpose — see `isDocumentExpired`'s own comment on why. */
function todayAsDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * `expiresAt` is a plain calendar date (Postgres `date`, no time or zone attached) — comparing it
 * as a `Date` timestamp against `Date.now()` would compare it at UTC midnight, which for Uruguay
 * (UTC-3) is 9 PM the evening before: a document expiring "on the 15th" would read as already
 * expired from 9 PM on the 14th, local time. Comparing the two as plain `YYYY-MM-DD` strings
 * instead (lexical order matches chronological order for this shape) compares calendar dates in
 * the viewer's own local timezone, with no such drift. `vitest.config.ts` pins the test runtime's
 * `TZ` to `America/Montevideo` specifically so a regression here fails locally, not only in
 * production for users west of UTC.
 */
export function isDocumentExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  const expiryDatePart = expiresAt.split('T')[0];
  return expiryDatePart < todayAsDateString();
}

/**
 * The status this screen actually shows for a document: `approved` reads as `outdated` once its
 * `expiresAt` has passed, the same computation `driver-document/handlers/
 * list-driver-documents.handler.ts` does server-side for the one document type (`libreta`) that
 * has reached that stage today. `storage_documents` already has `outdated` in its own status
 * CHECK (`1788900000000-CreateStorageDocuments.js`), so this is very likely a computation
 * Milestone 3's own list handler will also do server-side — doing it here too is redundant-safe
 * (same answer either way), not a second source of truth: this function only ever downgrades
 * `approved`, never a status the server already sent as something else.
 */
export function effectiveDocumentStatus(doc: Pick<StorageDocument, 'status' | 'expiresAt'>): StorageDocumentStatus {
  if (doc.status === 'approved' && isDocumentExpired(doc.expiresAt)) return 'outdated';
  return doc.status;
}

/**
 * Client-side only, for immediate upload feedback — real backend validation is the source of
 * truth (`request-upload-url.handler.ts` rejects anything else with `VALIDATION_ENUM_INVALID`);
 * this only ever saves a user a round trip for an obviously wrong file.
 *
 * **Fixed (NEX-1164 audit)**: this list used to be `['application/pdf', 'image/jpeg', 'image/png']`,
 * written before `nexvia-api-web`'s storage-documents handlers existed (see the git history on this
 * file). They exist now, and their real list —
 * `nexvia-api-web/src/modules/storage-document/constants/storage-document.constants.ts`'s
 * `ALLOWED_MIME_TYPES` — is five types, not three: the old three plus `image/heic` and
 * `image/webp`. The gap meant a depósito's own phone photo (HEIC is the iPhone camera default) or
 * a WEBP export was refused here before ever reaching a backend that would have accepted it.
 */
export const ACCEPTED_DOCUMENT_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp'
];

export const MAX_DOCUMENT_FILE_SIZE_BYTES = 10_485_760; // 10 MB, mirrors driver-document's FILE_SIZE.MAX_BYTES
/** Audit S6 — the API's floor (`storage-document.constants.ts` `FILE_SIZE.MIN_BYTES`, 10 KB): a smaller
 *  file is refused with `STORAGE_DOC_FILE_TOO_SMALL`, so the row says it before uploading. */
export const MIN_DOCUMENT_FILE_SIZE_BYTES = 10_240;

/** NEX-1164 — `DocumentsCard`'s anchor id: the approval banner's upload CTA ("Subir documentos" /
 *  "Volver a subir documentos") scrolls to it, there is no separate documents page. */
export const DOCUMENTS_SECTION_ID = 'documentacion';
